import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackLink, Button, Icon, NavRail, PageHeader } from "@ardept/design-system";
import { ApiError, AuthExpired } from "../api/fetcher.js";
import { useMe, useRenderedDocument, useRenderedDocumentPdf } from "../api/queries.js";
import { railVendorName } from "../auth/railVendorName.js";
import {
  byteSizeWords,
  classifyDocumentFailure,
  createdWords,
  DOCUMENT_FAILURE_WORDS,
  nearEmptyNote,
  pageCountWords,
  PDF_UNAVAILABLE_WORDS,
  templateWords,
} from "./_documentWords.js";
import { useObjectUrl } from "./objectUrl.js";
import "./DocumentDetailScreen.css";
import "./screens.css";

/**
 * #1969 — the staff page for one rendered document (`/documents/:documentId`).
 *
 * The engine's `render_document` tool stores the PDF per tenant and returns a staff link to this
 * route. A clicked link carries no bearer, so this page fetches the metadata and the PDF with the
 * staff bearer, shows the PDF inline from an object URL, and downloads it from the same URL.
 *
 * NavRail: `jobs`. There is no documents list and no new rail item (decision 7 / A9: the link is
 * the way in). Every rendered document belongs to the job that made it, and this page links to
 * that job, so Jobs is the surface it sits under; `drafts` is the email-drafts surface.
 *
 * Words, not identifiers (L-97 d): no document id, job id, hash, template id or `near_empty`
 * flag is ever printed — `_documentWords.ts` turns each engine value into a sentence.
 */
export function DocumentDetailScreen(): JSX.Element {
  const { documentId = "" } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { data: me } = useMe();

  const { data: doc, isLoading, error } = useRenderedDocument(documentId);
  // Wait for the metadata: a masked 404 then costs one request, not two.
  const pdf = useRenderedDocumentPdf(documentId, !!doc);
  const pdfUrl = useObjectUrl(pdf.data);

  // Operator display for NavRail from /me (the EmailDraftDetailScreen shape).
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  if (isLoading && !doc) {
    return (
      <div className="app">
        <NavRail active="jobs" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
        <main className="main">
          <div className="doc-loading">Loading document...</div>
        </main>
      </div>
    );
  }

  if (error || !doc) {
    const failure = classifyDocumentFailure(
      error instanceof ApiError ? error.status : undefined,
      error instanceof AuthExpired,
    );
    // No error and no document means there was no id to ask for: that reads as not found too.
    const words = DOCUMENT_FAILURE_WORDS[error ? failure : "not_found"];
    return (
      <div className="app">
        <NavRail active="jobs" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
        <main className="main">
          <BackLink to="/jobs" navigate={navigate} label="Back to jobs" />
          <div className="doc-error" role="alert">
            {words}
          </div>
        </main>
      </div>
    );
  }

  const note = nearEmptyNote(doc.pages);
  const filename = doc.filename || "document.pdf";

  function download(): void {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // The metadata loaded, so a PDF failure is never "not found": it is a failed load, or a
  // session that ran out between the two reads.
  const pdfFailureWords =
    pdf.error instanceof AuthExpired ? DOCUMENT_FAILURE_WORDS.session_expired : PDF_UNAVAILABLE_WORDS;

  return (
    <div className="app">
      <NavRail active="jobs" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
      <main className="main">
        <BackLink to="/jobs" navigate={navigate} label="Back to jobs" />
        <PageHeader
          title={doc.title}
          subtitle={doc.reference || undefined}
          actions={
            <Button variant="primary" onClick={download} disabled={!pdfUrl}>
              <Icon name="download" size={14} aria-hidden="true" />
              Download PDF
            </Button>
          }
        />

        <dl className="doc-meta">
          {doc.reference ? (
            <>
              <dt>Reference</dt>
              <dd>{doc.reference}</dd>
            </>
          ) : null}
          <dt>Pages</dt>
          <dd>{pageCountWords(doc.page_count)}</dd>
          <dt>Created</dt>
          <dd>{createdWords(doc.created_at)}</dd>
          <dt>Template</dt>
          <dd>{templateWords(doc.template_id, doc.template_version)}</dd>
          <dt>File size</dt>
          <dd>{byteSizeWords(doc.byte_size)}</dd>
          <dt>Job</dt>
          <dd>
            <a
              className="doc-meta__link"
              href={`/jobs/${doc.job_id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/jobs/${doc.job_id}`);
              }}
            >
              Open the job that made this document
            </a>
          </dd>
        </dl>

        {note ? (
          <div className="doc-note" role="note">
            {note}
          </div>
        ) : null}

        <section className="doc-viewer" aria-label="Document preview">
          {pdfUrl ? (
            <iframe className="doc-viewer__frame" src={pdfUrl} title={`${doc.title} (PDF)`} />
          ) : pdf.error ? (
            <div className="doc-error" role="alert">
              {pdfFailureWords}
            </div>
          ) : (
            <div className="doc-loading">Loading the PDF...</div>
          )}
        </section>
      </main>
    </div>
  );
}
